/**
 * Date input
 * Styled react date picker wrapper
 */
import { Box, TextField } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';

import InputLabel from '@components/InputLabel';

import { createDateAsUTC } from '@utils/date';

import type { EffectiveDateTimeInputProps } from './types';

interface DateInputProps extends Omit<
  EffectiveDateTimeInputProps,
  'onChange' | 'value' | 'min' | 'max' | 'type'
> {
  value?: Date | number | string | null;
  onChange?: (value: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
}

const FORMAT = 'YYYY-MM-DD';
const DateInput: React.FC<DateInputProps> = ({
  label = '',
  error = '',
  value,
  onChange,
  onBlur,
  name,
  required = false,
  maxDate,
  minDate,
}) => {
  const [selectedDate, setSelectedDate] = React.useState<string>('');
  const [min, setMin] = React.useState<string>();
  const [max, setMax] = React.useState<string>();

  React.useEffect(() => {
    if (value) {
      const date = dayjs(value).format(FORMAT);
      setSelectedDate(date);
    } else {
      setSelectedDate('');
    }
  }, [value]);

  React.useEffect(() => {
    if (minDate) {
      const date = dayjs(minDate).format(FORMAT);
      setMin(date);
    } else {
      setMin(undefined);
    }
  }, [minDate]);

  React.useEffect(() => {
    if (maxDate) {
      const date = dayjs(maxDate).format(FORMAT);
      setMax(date);
    } else {
      setMax(undefined);
    }
  }, [maxDate]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;
    if (!value) {
      return onChange?.(null);
    }
    const date = createDateAsUTC(dayjs(value).toDate());
    return onChange?.(date);
  };

  return (
    <Box sx={{ w: 'inherit' }}>
      <InputLabel label={label} required={required} />
      <TextField
        fullWidth
        error={Boolean(error)}
        helperText={error ? error : ''}
        name={name}
        type="date"
        onBlur={onBlur}
        onChange={handleChange}
        placeholder={FORMAT.toUpperCase()}
        value={selectedDate}
        InputProps={{
          inputProps: {
            min,
            max,
          },
        }}
        sx={{ minHeight: 47 }}
      />
    </Box>
  );
};

export default DateInput;
export type { DateInputProps };
